import './InitiativeScopeTable.scss';

import { COPY_SPECIFIC } from '@sonar-software/sonar-ts-constants';
import { createAsyncSelectors } from '@lib/selectors';
import { getClosestSelectionTable } from '@lib/popups';
import { getInitiativeScopeTableColumns } from './InitiativeScopeTableColumns';
import { InitiativeScopeTableActionBar } from './InitiativeScopeTableActionBar';
import isEqual from 'lodash/isEqual';
import { keyPaginationDefaultQueryParams } from '@ui/KeyPagination';
import { KeyPaginationQueryParams } from '@sonar-software/sonar-ts-types';
import { SortByFilterQueryParams } from '@periscopes/general/filters';
import { SorterResult } from 'antd/es/table/interface';
import { TableProps } from 'antd';

import { coreActions, InitiativeSonarObject } from '@sonar-software/sonar-core';
import {
	paginationDefaultQueryParams,
	paginationFallbackQueryParams,
	PaginationQueryParams,
	PeriscopePagination
} from '@periscopes/general';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { SelectionTable, SelectionTableQueryParams } from '@ui/tables';
import {
	useAction,
	useOnMount,
	usePardotEnabled,
	usePrevious,
	useQueryParams,
	useSelectionTable,
	UseSelectionTableResult,
	useSelectionTableWithInversion,
	useSonarSelector
} from '@hooks';

function getRowKey(row: InitiativeSonarObject & { id: number }) {
	return row.sonarObjectId;
}

const selectors = createAsyncSelectors((state) => state.core.initiatives.sonarObjects.get);

type ScopeDataQueryParams = KeyPaginationQueryParams & {
	filters: string;
	ordering: string;
};

export type InitiativeScopeTableQueryParams = PaginationQueryParams &
	SortByFilterQueryParams &
	SelectionTableQueryParams & {
		initiativeId: number;
		objectNameSearchText?: string;
		objectTypes: string[];
	};

export interface InitiativeScopeTableProps {
	count: number;
	dynamicNameFilter: string;
	initiativeId: number;
	nameFilter?: string;
	selectNamespace: string;
}

export const InitiativeScopeTable: React.FC<InitiativeScopeTableProps> = ({
	count,
	dynamicNameFilter,
	initiativeId,
	nameFilter,
	selectNamespace
}) => {
	const scrollableListRef = useRef<HTMLDivElement>(null);
	const showPardotTypes = usePardotEnabled();
	const resource = useSonarSelector(selectors.data);
	const columns = useMemo(() => getInitiativeScopeTableColumns(showPardotTypes), [ showPardotTypes ]);
	const previousNameFilter = usePrevious(nameFilter, nameFilter);
	const previousCount = usePrevious(count, count);

	const loadScopes = useAction(coreActions.requestGetCoreInitiativesSonarObjects);

	const defaultParams: InitiativeScopeTableQueryParams = {
		...paginationDefaultQueryParams,
		count: 5,
		initiativeId,
		objectNameSearchText: nameFilter,
		objectTypes: [],
		sortBy: 'name',
		sortDirection: 'descending'
	};

	const selectionData = useSelectionTableWithInversion({
		getRowKey: getRowKey,
		results: resource?.results,
		totalCount: resource?.totalCount
	});

	const selectionDataRef = useRef<UseSelectionTableResult<InitiativeSonarObject> | null>(null);

	const { queryParams: dynamicQueryParams } = useQueryParams<ScopeDataQueryParams>({
		defaultParams: {
			...keyPaginationDefaultQueryParams,
			count: 5,
			filters: dynamicNameFilter,
			ordering: ''
		}
	});

	const { queryParams, updateParams } = useQueryParams<InitiativeScopeTableQueryParams>({
		defaultParams,
		fallbackParams: paginationFallbackQueryParams,
		onUpdate: (newParams, prev, combined) => {
			const didObjectTypesChange = newParams.objectTypes && !isEqual(newParams.objectTypes, prev?.objectTypes);

			loadScopes(combined);

			if (didObjectTypesChange) {
				selectionData.resetSelection();
			}
		}
	});

	useOnMount(() => {
		loadScopes(defaultParams);
	});

	useEffect(() => {
		if (previousNameFilter !== nameFilter) {
			updateParams({
				objectNameSearchText: nameFilter
			});
		}
	}, [ nameFilter, previousNameFilter, updateParams ]);

	selectionDataRef.current = useSelectionTable({
		getRowKey,
		namespace: selectNamespace,
		queryParams: dynamicQueryParams,
		resourceIdsUri: `v3/initiatives/${initiativeId}/scope/bulk`,
		results: resource?.results
	});

	const handleOnTableChange = useCallback<NonNullable<TableProps<InitiativeSonarObject>['onChange']>>(
		(pagination, filters, sorter: SorterResult<InitiativeSonarObject>) => {
			updateParams({
				objectTypes: (filters.type as string[]) ?? [],
				sortBy: sorter.columnKey as string,
				sortDirection: `${sorter.order || 'descend'}ing`
			});
		},
		[ updateParams ]
	);

	useEffect(() => {
		if (previousCount !== count) {
			loadScopes(queryParams);
		}
	}, [ loadScopes, previousCount, queryParams, count ]);

	return (
		<>
			<SelectionTable
				actionBar={
					<InitiativeScopeTableActionBar
						initiativeId={ initiativeId }
						selectNamespace={ selectNamespace } />
				}
				className='initiative-scope-table data-dictionary-table'
				columns={ columns }
				dataSource={ resource?.results }
				getPopupContainer={ getClosestSelectionTable }
				isActionBarAbove={ false }
				onChange={ handleOnTableChange }
				ref={ scrollableListRef }
				rowKey={ getRowKey }
				selectionData={ selectionDataRef.current }
				selectionType={ COPY_SPECIFIC.SCOPES } />
			<PeriscopePagination
				loading={ false }
				onChange={ updateParams }
				queryParams={ queryParams }
				scrollRef={ scrollableListRef }
				totalCount={ Number(resource?.totalCount) } />
		</>
	);
};
